import produce from 'immer';
import { types } from './types';
import { ActData } from '../../types/global/Cards.types';
import { ITalentReview, IWorkProfile, TalentSearchFilter } from '../../types/global/helper';
import { getIdForIbjects, restoreInitialState } from '../../utils/AppUtils';
import { types as projectTypes } from '../project/types';
import { types as userProfileTypes } from '../user/types';

export interface ITalentProfileInitialState {
    gettingTalentProfile: boolean;
    selectedTalentProfile?: IWorkProfile[];
    talentProfiles: IWorkProfile[];
    curentProfileInCreation?: IWorkProfile;
    creatingProfileInProgress: boolean;
    creatingProfileErrorMsg: any;
    /** The profile in preview. This could be the profile of the logged in user or the
     * profile of external user being seen. It is an array of profiles
     */
    currentUserTalentProfile: IWorkProfile[] | null;
    gettingProfileErrorMsg: any;
    deletingProfile: boolean;
    deletingProfileErrorMsg: any;
    currentSelectionPastWork: any;
    currentPastWorkErrMsg: any;
    gettingPastWork: boolean;
    searchResult: Array<IWorkProfile>;
    searchingInProgress: boolean;
    talentFilter?: TalentSearchFilter;
    filterMode: boolean;
    showFilter: boolean;
    discover: {};
    gettingDiscover: boolean;
    discoverErrMsg: any;
    acts: Array<ActData>;
    gettingActs: boolean;
    actsErrMsg: null;
    userActs: Array<ActData>;
    gettingUserActs: boolean;
    userActsErrMsg: any;
    currentAct: Array<ActData>;
    gettingCurrentAct: boolean;
    creatingAct: boolean;
    createActErrMsg: any;
    updatingAct: boolean;
    deletingAct: boolean;
    deleteActErrMsg: any;
    currentSelectedTalentReviews: ITalentReview[];
    currentSelectedTalentReviewPage?: number;
    gettingTalentReviews?: boolean;
    talentReviewErrMsg?: any;
    likingInProgress?: boolean;
    tempFeaturePicture?: string;
    addingCredit?: boolean;
}

const initialState: ITalentProfileInitialState = {
    gettingTalentProfile: false,
    talentProfiles: [],
    creatingProfileInProgress: false,
    creatingProfileErrorMsg: null,
    gettingProfileErrorMsg: null,
    deletingProfile: false,
    deletingProfileErrorMsg: null,
    currentSelectionPastWork: null,
    currentPastWorkErrMsg: null,
    gettingPastWork: false,
    searchResult: [],
    searchingInProgress: false,
    filterMode: false,
    showFilter: false,
    discover: {},
    gettingDiscover: false,
    discoverErrMsg: null,
    acts: [],
    gettingActs: false,
    actsErrMsg: null,
    userActs: [],
    gettingUserActs: false,
    userActsErrMsg: null,
    currentAct: [],
    gettingCurrentAct: false,
    creatingAct: false,
    createActErrMsg: null,
    updatingAct: false,
    deletingAct: false,
    deleteActErrMsg: null,
    curentProfileInCreation: undefined,
    talentFilter: undefined,
    currentSelectedTalentReviews: [],
    currentSelectedTalentReviewPage: 1,
    gettingTalentReviews: false,
    currentUserTalentProfile: [],
    likingInProgress: false,
};

const reducer = (state = initialState, action: any) => {
    const { type, payload } = action;

    switch (type) {
        case types.UPDATE_TALENT_PROFILE:
            return produce(state, draftState => {
                const { uploads, id } = payload;
                const { temp_feature_picture } = uploads || {};
                if (uploads && uploads.feature_picture && temp_feature_picture) {
                    const index = draftState.currentUserTalentProfile?.findIndex(profile => profile._id === id);
                    if (index && index > -1 && temp_feature_picture && draftState.currentUserTalentProfile) {
                        draftState.tempFeaturePicture = draftState.currentUserTalentProfile[index].feature_picture;
                        draftState.currentUserTalentProfile[index].feature_picture = temp_feature_picture;
                    }
                }
                draftState.creatingProfileInProgress = true;
            });
        case types.ADD_CREDIT:
        case types.UPDATE_CREDIT:
        case types.DELETE_CREDIT:
            return produce(state, draftState => {
                draftState.addingCredit = true;
            });
        case types.ADD_CREDIT_SUCCESS:
            return produce(state, draftState => {
                draftState.addingCredit = false;

                // Ensure profile list is available
                if (draftState.currentUserTalentProfile && Array.isArray(draftState.currentUserTalentProfile)) {
                    const profileIndex = draftState.currentUserTalentProfile.findIndex(
                        prof => prof._id === payload.profile,
                    );

                    // If found, push to the credits array safely
                    if (profileIndex !== -1) {
                        const profile = draftState.currentUserTalentProfile[profileIndex];

                        // Initialize credits array if not present
                        if (!Array.isArray(profile.credits)) {
                            profile.credits = [];
                        }

                        profile.credits.push(payload);
                    }
                }
            });

        case types.ADD_CREDIT_FAIL:
        case types.DELETE_CREDIT_FAIL:
        case types.UPDATE_CREDIT_FAIL:
            return produce(state, draftState => {
                draftState.addingCredit = false;
            });

        case types.UPDATE_CREDIT_SUCCESS:
            return produce(state, draftState => {
                if (draftState.currentUserTalentProfile) {
                    /** Get the profile in question */
                    const profile = draftState.currentUserTalentProfile.find(prof => prof._id === payload.profile);
                    if (profile) {
                        const currentCreditInEdit = profile.credits?.findIndex(cred => cred._id === payload._id);
                        if (currentCreditInEdit && currentCreditInEdit > -1 && profile.credits) {
                            profile.credits[currentCreditInEdit] = payload;
                        }
                    }
                }
                draftState.addingCredit = false;
            });
        case types.DELETE_CREDIT_SUCCESS:
            return produce(state, draftState => {
                draftState.addingCredit = false;

                if (draftState.currentUserTalentProfile && Array.isArray(draftState.currentUserTalentProfile)) {
                    const { _id } = payload.payload;
                    const profile = draftState.currentUserTalentProfile.find(prof => prof._id === _id);

                    if (profile && Array.isArray(profile.credits)) {
                        profile.credits = profile.credits.filter(cred => cred._id !== payload.documentId);
                    }
                }
            });

        case types.CREATE_TALENT_PROFILE:
        case types.PUBLISH_TALENT_PROFILE:
        case types.UNPUBLISH_TALENT_PROFILE:
        case types.UPLOAD_IMAGE:
            return produce(state, draftState => {
                draftState.creatingProfileInProgress = true;
            });
        case types.CREATE_TALENT_PROFILE_SUCCESS:
        case types.UNPUBLISH_TALENT_PROFILE_SUCCESS:
        case types.PUBLISH_TALENT_PROFILE_SUCCESS:
        case types.UPLOAD_IMAGE_SUCCESS:
            return produce(state, draftState => {
                draftState.creatingProfileInProgress = false;
                draftState.curentProfileInCreation = payload;
                draftState.currentUserTalentProfile!.push(payload);
            });
        case types.UPDATE_TALENT_PROFILE_SUCCESS:
            return produce(state, draftState => {
                draftState.curentProfileInCreation = payload;
                draftState.creatingProfileInProgress = false;
                const index = draftState.currentUserTalentProfile?.findIndex(profile => profile._id === payload._id);
                if (index !== -1 && index !== undefined && draftState.currentUserTalentProfile) {
                    draftState.currentUserTalentProfile[index] = payload; // Replace the existing profile
                    draftState.tempFeaturePicture = undefined;
                }
            });
        case types.UPDATE_TALENT_PROFILE_FAIL:
            return produce(state, draftState => {
                const { additionalPayload } = payload;
                const { uploads, id } = additionalPayload || {};
                const { temp_feature_picture } = uploads || {};
                draftState.creatingProfileInProgress = false;
                draftState.creatingProfileErrorMsg = payload;
                if (uploads && uploads.feature_picture && temp_feature_picture) {
                    const index = draftState.currentUserTalentProfile?.findIndex(profile => profile._id === id);
                    if (index && index > -1 && temp_feature_picture && draftState.currentUserTalentProfile) {
                        draftState.currentUserTalentProfile[index].feature_picture = draftState.tempFeaturePicture;
                        draftState.tempFeaturePicture = undefined;
                    }
                }
            });
        case types.CREATE_TALENT_PROFILE_FAIL:
        case types.UNPUBLISH_TALENT_PROFILE_FAIL:
        case types.PUBLISH_TALENT_PROFILE_FAIL:
        case types.UPLOAD_IMAGE_FAIL:
            return produce(state, draftState => {
                draftState.creatingProfileInProgress = false;
                draftState.creatingProfileErrorMsg = payload;
            });
        case types.GET_ALL_TALENT_PROFILES:
        case types.GET_TALENT_PROFILE_DETAILS:
        case types.GET_CURRENT_USER_TALENT_PROFILE:
            return produce(state, draftState => {
                draftState.gettingTalentProfile = true;
                draftState.selectedTalentProfile = undefined;
                draftState.currentUserTalentProfile = null;
            });
        case types.GET_ALL_TALENT_PROFILES_SUCCESS:
            return produce(state, draftState => {
                draftState.gettingTalentProfile = false;
                // Parse This payload
                draftState.talentProfiles = payload;
                draftState.searchResult = payload;
            });
        case types.GET_TALENT_PROFILE_DETAILS_SUCCESS:
            return produce(state, draftState => {
                draftState.gettingTalentProfile = false;
                draftState.selectedTalentProfile = payload;
                // draftState.curentProfileInCreation = payload; // TODO FIX
                draftState.currentUserTalentProfile = payload;
            });
        case types.GET_TALENT_PROFILE_DETAILS_FAIL:
        case types.GET_ALL_TALENT_PROFILES_FAIL:
        case types.GET_CURRENT_USER_TALENT_PROFILE_FAIL:
        case types.FILTER_TALENT:
            return produce(state, (draftState: typeof initialState) => {
                draftState.gettingTalentProfile = true;
                draftState.gettingProfileErrorMsg = payload;
                draftState.currentUserTalentProfile = null;
            });
        case types.GET_CURRENT_USER_TALENT_PROFILE_SUCCESS:
            return produce(state, (draftState: typeof initialState) => {
                draftState.gettingTalentProfile = false;
                draftState.currentUserTalentProfile = payload;
                draftState.curentProfileInCreation = payload;
            });
        case types.DELETE_TALENT_PROFILE:
            return produce(state, draftState => {
                draftState.deletingProfile = true;
            });
        case types.DELETE_TALENT_PROFILE_SUCCESS:
            return produce(state, draftState => {
                draftState.deletingProfile = false;
            });
        case types.DELETE_TALENT_PROFILE_FAIL:
            return produce(state, draftState => {
                draftState.deletingProfileErrorMsg = payload;
            });
        case types.GET_TALENT_PAST_WORK:
            return produce(state, draftState => {
                draftState.gettingPastWork = true;
            });
        case types.GET_TALENT_PAST_WORK_SUCCESS:
            return produce(state, draftState => {
                draftState.gettingPastWork = false;
                draftState.currentSelectionPastWork = payload;
            });
        case types.GET_TALENT_PAST_WORK_FAIL:
            return produce(state, draftState => {
                draftState.gettingPastWork = false;
                draftState.currentPastWorkErrMsg = payload;
            });
        case types.SET_PROFILE_FOR_EDIT:
            return produce(state, draftState => {
                draftState.curentProfileInCreation = payload;
            });
        case types.TOGGLE_TALENT_FILTER:
            return produce(state, (draftState: any) => {
                draftState.showFilter = !state.showFilter;
            });
        case types.GET_DISCOVER:
            return produce(state, draftState => {
                draftState.gettingDiscover = true;
            });
        case types.GET_DISCOVER_FAIL:
            return produce(state, draftState => {
                draftState.gettingDiscover = false;
                draftState.discoverErrMsg = payload;
            });
        case types.CREATE_ACT_FAIL:
            return produce(state, draftState => {
                draftState.creatingAct = false;
                draftState.createActErrMsg = payload;
            });
        case types.DELETE_ACT:
            return produce(state, draftState => {
                draftState.deletingAct = true;
            });
        case types.DELETE_ACT_SUCCESS:
            return produce(state, draftState => {
                draftState.deletingAct = false;
                const filteredActs = draftState.acts.filter((act: any) => act.id !== payload);
                draftState.acts = filteredActs;
            });
        case types.DELETE_ACT_FAIL:
            return produce(state, draftState => {
                draftState.deletingAct = false;
                draftState.deleteActErrMsg = payload;
            });
        case types.GET_MY_ACTS:
            return produce(state, draftState => {
                draftState.gettingActs = true;
            });
        case types.GET_MY_ACTS_SUCCESS:
            return produce(state, draftState => {
                draftState.gettingActs = false;
                draftState.userActs = payload;
            });
        case types.GET_MY_ACTS_FAIL:
            return produce(state, draftState => {
                draftState.gettingActs = false;
                draftState.userActsErrMsg = payload;
            });
        case types.SET_TALENT_DETAILS:
            return produce(state, draftState => {
                draftState.selectedTalentProfile = payload;
            });
        case types.GET_TALENT_REVIEWS:
            return produce(state, draftState => {
                draftState.gettingTalentReviews = true;
            });
        case types.GET_TALENT_REVIEWS_SUCCESS:
            return produce(state, draftState => {
                const { data, currentPage } = payload;
                draftState.gettingTalentReviews = false;
                draftState.currentSelectedTalentReviews = data;
                draftState.currentSelectedTalentReviewPage = Number(currentPage);
            });
        case types.GET_TALENT_REVIEWS_FAIL:
            return produce(state, draftState => {
                draftState.gettingTalentReviews = false;
                draftState.talentReviewErrMsg = payload;
            });

        case types.CREATE_TALENT_REVIEW:
            return produce(state, draftState => {
                draftState.gettingTalentReviews = true;
            });
        case types.CREATE_TALENT_REVIEW_SUCCESS:
            return produce(state, draftState => {
                draftState.gettingTalentReviews = false;
                draftState.currentSelectedTalentReviews = [payload, ...draftState.currentSelectedTalentReviews];
            });
        case types.CREATE_TALENT_REVIEW_FAIL:
            return produce(state, draftState => {
                draftState.gettingTalentReviews = false;
                draftState.talentReviewErrMsg = payload;
            });
        case types.UPDATE_TALENT_REVIEW:
            return produce(state, draftState => {
                draftState.gettingTalentReviews = true;
            });
        case types.UPDATE_TALENT_REVIEW_SUCCESS:
            return produce(state, draftState => {
                draftState.gettingTalentReviews = false;
                const index = draftState.currentSelectedTalentReviews.findIndex(review => review._id === payload._id);
                const profileId = getIdForIbjects(payload.talent);
                const selectedProfile = draftState.selectedTalentProfile?.find(prof => prof._id === profileId);
                if (index !== -1) {
                    draftState.currentSelectedTalentReviews[index] = payload;
                    // Update the rating in the selected profile
                    if (draftState.selectedTalentProfile) {
                        const totalReviews = draftState.currentSelectedTalentReviews.length;
                        const totalRating = draftState.currentSelectedTalentReviews.reduce(
                            (sum, review) => sum + review.rating,
                            0,
                        );
                        if (selectedProfile) {
                            selectedProfile.rating = totalRating / totalReviews;
                        }
                    }
                }
            });
        case types.UPDATE_TALENT_REVIEW_FAIL:
            return produce(state, draftState => {
                draftState.gettingTalentReviews = false;
                draftState.talentReviewErrMsg = payload;
            });
        case types.DELETE_TALENT_REVIEW:
            return produce(state, draftState => {
                draftState.gettingTalentReviews = true;
            });
        case types.DELETE_TALENT_REVIEW_SUCCESS:
            return produce(state, draftState => {
                draftState.gettingTalentReviews = false;
                draftState.currentSelectedTalentReviews = draftState.currentSelectedTalentReviews.filter(
                    review => review._id !== payload,
                );
            });
        case types.DELETE_TALENT_REVIEW_FAIL:
            return produce(state, draftState => {
                draftState.gettingTalentReviews = false;
                draftState.talentReviewErrMsg = payload;
            });
        case types.LIKE_TALENT_REVIEW_SUCCESS:
            return produce(state, draftState => {
                const currentReviewInContext = draftState.currentSelectedTalentReviews.find(
                    rev => rev._id === payload.talentReview,
                );
                if (currentReviewInContext) {
                    /** Get the temp Object */
                    currentReviewInContext.like_object = [payload];
                }
                draftState.likingInProgress = false;
            });
        case types.LIKE_TALENT_REVIEW:
            return produce(state, draftState => {
                const currentReviewInContext = draftState.currentSelectedTalentReviews.find(
                    rev => rev._id === payload.reviewId,
                );
                if (currentReviewInContext) {
                    const tempObject: any = {
                        id: 'temp',
                        talentReview: payload.reviewId,
                    };
                    currentReviewInContext.total_likes! += 1;
                    currentReviewInContext.user_has_liked = true;
                    currentReviewInContext.like_object = [tempObject];
                }
                draftState.likingInProgress = true;
            });
        case types.LIKE_TALENT_REVIEW_FAIL:
            return produce(state, draftState => {
                const currentReviewInContext = draftState.currentSelectedTalentReviews.find(rev => rev._id === 'temp');
                if (currentReviewInContext) {
                    currentReviewInContext.total_likes = currentReviewInContext.total_likes ?? 0 - 1;
                    currentReviewInContext.user_has_liked = false;
                    currentReviewInContext.like_object = [];
                }
                draftState.likingInProgress = false;
            });
        case types.DISLIKE_TALENT_REVIEW:
            return produce(state, draftState => {
                const currentReviewInContext = draftState.currentSelectedTalentReviews.find(
                    rev => rev._id === payload.reviewId,
                );
                if (currentReviewInContext) {
                    const tempObject: any = {
                        id: 'temp',
                        talentReview: payload.reviewId,
                    };
                    currentReviewInContext.total_dislikes! += 1;
                    currentReviewInContext.user_has_disliked = true;
                    currentReviewInContext.dislike_object = [tempObject];
                }
                draftState.likingInProgress = true;
            });
        case types.DISLIKE_TALENT_REVIEW_SUCCESS:
            return produce(state, draftState => {
                const currentReviewInContext = draftState.currentSelectedTalentReviews.find(
                    rev => rev._id === payload.talentReview,
                );
                if (currentReviewInContext) {
                    /** Get the temp Object */
                    currentReviewInContext.dislike_object = [payload];
                }
                draftState.likingInProgress = false;
            });
        case types.DISLIKE_TALENT_REVIEW_FAIL:
            return produce(state, draftState => {
                const currentReviewInContext = draftState.currentSelectedTalentReviews.find(rev => rev._id === 'temp');
                if (currentReviewInContext) {
                    currentReviewInContext.total_likes! -= 1;
                    currentReviewInContext.user_has_disliked = false;
                    currentReviewInContext.dislike_object = [];
                }
                draftState.likingInProgress = false;
            });
        case projectTypes.DELETE_A_LIKE_FAIL:
            return produce(state, draftState => {
                const { morePayload } = payload;
                if (morePayload.like_category === 'talentReview') {
                    const index = draftState.currentSelectedTalentReviews.findIndex(
                        review => review._id === morePayload.reviewId,
                    );
                    if (index !== -1) {
                        const review = draftState.currentSelectedTalentReviews[index];
                        if (morePayload.like_type === 'like') {
                            review.user_has_liked = true;
                            review.total_likes = (review.total_likes || 0) + 1;
                        } else {
                            review.user_has_disliked = true;
                            review.total_dislikes = (review.total_dislikes || 0) + 1;
                        }
                    }
                }
            });
        case projectTypes.DELETE_A_LIKE:
            return produce(state, draftState => {
                const { morePayload } = payload;
                if (morePayload.like_category === 'talentReview') {
                    const index = draftState.currentSelectedTalentReviews.findIndex(
                        review => review._id === morePayload.reviewId,
                    );
                    if (index !== -1) {
                        const review = draftState.currentSelectedTalentReviews[index];
                        if (morePayload.like_type === 'like') {
                            review.user_has_liked = false;
                            review.total_likes! -= 1;
                            review.like_object = [];
                        } else {
                            review.user_has_disliked = false;
                            review.total_dislikes! -= 1;
                            review.dislike_object = [];
                        }
                    }
                }
            });
        case userProfileTypes.GET_A_USER_PROFILE_SUCCESS:
            return produce(state, draftState => {
                const { profiles } = payload || {};
                if (profiles) {
                    draftState.currentUserTalentProfile = profiles;
                }
            });
        case types.RESET_TALENT_PROFILE:
            return restoreInitialState(state, initialState);
        default:
            return state;
    }
};

export { reducer };
